import React from 'react';
import { FiBarChart2, FiTrendingUp, FiUsers, FiClock, FiAward, FiZap, FiShield, FiGlobe } from 'react-icons/fi';

const CollegeDashboard = () => {
  const dashboardFeatures = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Enrollment & Demographics',
      description: 'See real-time enrollment numbers across programs, years, and demographics. Understand who your students are, where they come from, and how enrollment is trending. This data helps you plan resources, marketing, and academic offerings.',
      stat: '90% of principals use enrollment data for strategic planning',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Academic Performance Overview',
      description: 'Track student performance across departments, programs, and semesters. Identify programs that are excelling and those that need attention. Understand pass rates, grade distributions, and academic trends at a glance.',
      stat: '85% of deans use performance data to improve academic quality',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Attendance & Student Engagement',
      description: 'Monitor attendance patterns across campus. Identify courses with low attendance, programs with engagement issues, and individual students who may need intervention. Early identification leads to better student outcomes.',
      stat: 'Colleges with attendance analytics reduce dropouts by 35%',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Faculty Workload & Performance',
      description: 'Understand faculty teaching loads, research output, and student feedback. Make informed decisions about hiring, workload distribution, and professional development. Support your faculty to be their best.',
      stat: '80% of colleges improve faculty satisfaction with workload insights',
      color: 'bg-rose-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Financial & Operational Metrics',
      description: 'Track budgets, expenditures, and resource utilization across departments. Make data-driven decisions about resource allocation, cost optimization, and investment priorities.',
      stat: '70% of principals use financial analytics for budget decisions',
      color: 'bg-purple-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Accreditation & Compliance Dashboard',
      description: 'Monitor compliance metrics in real-time. Generate reports for NAAC, NIRF, and other accreditation bodies instantly. Always be ready for inspections and accreditation visits.',
      stat: '95% of colleges reduce accreditation stress with automated dashboards',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Real-Time Visibility',
      description: 'See what\'s happening across your college at any moment. No more waiting for monthly reports.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Data-Driven Decisions',
      description: 'Replace intuition with evidence. Make decisions that are backed by real data and insights.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Early Problem Identification',
      description: 'Spot issues before they become crises. Identify struggling students, underperforming programs, and resource gaps early.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Better Resource Allocation',
      description: 'Allocate budget, staff, and infrastructure where they\'re needed most. Optimize your college\'s efficiency.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Improved Student Outcomes',
      description: 'Use insights to support students better. Improve retention, graduation rates, and academic success.',
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: 'Accreditation Readiness',
      description: 'Always be ready for accreditation reviews. Generate comprehensive reports instantly.',
      icon: <FiAward className="w-5 h-5" />
    }
  ];

  const dashboardViews = [
    {
      role: 'Principal',
      description: 'See the big picture—overall college performance, enrollment trends, financial health, and strategic metrics.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      role: 'Dean / Academic Head',
      description: 'Focus on academic performance—program outcomes, faculty effectiveness, curriculum quality, and student success.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      role: 'Department Head',
      description: 'Drill down into department-level data—student performance, faculty workload, course outcomes, and resource needs.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      role: 'Administrator',
      description: 'Track operational efficiency—attendance, fee collection, admissions, and day-to-day operations.',
      icon: <FiClock className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Running a college is complex. You manage hundreds or thousands of students. You oversee faculty across multiple departments. You balance budgets, ensure quality, maintain accreditations, and satisfy stakeholder expectations. And you're expected to make decisions every day—some small, some strategic, all important.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But without data, you're flying blind. <strong className="text-slate-900">How many students are actually attending classes? Which programs are underperforming? Where are your resources best invested? Which students need intervention?</strong> These questions demand answers—and the answers demand data.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        An analytics dashboard gives deans and principals the visibility they need to lead effectively. Here's how it transforms decision-making.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">90%</div>
          <div className="text-sm text-slate-500">Principals use data for strategic planning</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">85%</div>
          <div className="text-sm text-slate-500">Deans use data to improve academic quality</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">35%</div>
          <div className="text-sm text-slate-500">Reduction in dropouts with attendance analytics</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">What a College Analytics Dashboard Delivers</h2>

      {/* Features List */}
      {dashboardFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">{item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Deans and Principals Need Analytics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dashboard Views */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Dashboards for Every Leader</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {dashboardViews.map((item, i) => (
            <div key={i} className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.role}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional vs. Analytics-Driven Leadership</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Decisions based on intuition</span></li>
              <li className="flex items-start gap-2">• <span>Information from multiple systems</span></li>
              <li className="flex items-start gap-2">• <span>React to problems when they arise</span></li>
              <li className="flex items-start gap-2">• <span>Difficult to justify decisions</span></li>
              <li className="flex items-start gap-2">• <span>Accreditation reports take weeks</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Analytics-Driven Leadership</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Decisions based on real evidence</span></li>
              <li className="flex items-start gap-2">• <span>All data in one dashboard</span></li>
              <li className="flex items-start gap-2">• <span>Identify and address issues early</span></li>
              <li className="flex items-start gap-2">• <span>Clear justification for decisions</span></li>
              <li className="flex items-start gap-2">• <span>Accreditation reports generated instantly</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for College Leadership</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Dashboard</h4>
            <p className="text-xs text-slate-600">See key metrics at a glance</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Trend Analysis</h4>
            <p className="text-xs text-slate-600">Track patterns over time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Predictive Insights</h4>
            <p className="text-xs text-slate-600">Identify risks early</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Accreditation Ready</h4>
            <p className="text-xs text-slate-600">Generate NAAC reports instantly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Access</h4>
            <p className="text-xs text-slate-600">View data from anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Secure Access</h4>
            <p className="text-xs text-slate-600">Role-based data visibility</p>
          </div>
        </div>
      </div>

      {/* The Human Impact */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">Leadership with Clarity, Not Guesswork</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind every data point is a student who needs support. A faculty member who deserves recognition. A program that needs attention. A resource that should be reallocated. The data gives you the insight to make these decisions with confidence and clarity.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Leadership without data is guesswork. Leadership with data is purpose-driven.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Lead Smarter with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Deans and principals make decisions that shape the future of their institutions—and the lives of thousands of students. These decisions deserve to be informed by data, not just intuition.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides the analytics dashboard you need to lead effectively. Real-time enrollment data, academic performance tracking, faculty insights, financial metrics, and accreditation readiness—all in one place.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for college leadership.</strong> Smarter decisions. Better outcomes. Stronger institutions.
        </p>
      </div>
    </>
  );
};

export default CollegeDashboard;